import { Playbook } from '../types';
import { PLAYBOOKS_URL } from '../config/api-config';
import { useAuth } from '../contexts/AuthContext';
import { createApiHeadersGetter } from '../utils/api-headers';

interface APIError {
  message: string;
  code?: string;
  details?: any;
  status?: number;
}

export class ApiError extends Error {
  status: number;
  details?: any;

  constructor(message: string, status: number, details?: any) {
    super(message);
    this.status = status;
    this.details = details;
    this.name = 'ApiError';
  }
}

export interface PlaybookVersion {
  playbookId: string;
  version: number;
  publishedAt: string;
  publishedBy: string;
  publishedByEmail: string;
  changeNote: string;
  isRollback: boolean;
  rolledBackFromVersion: number | null;
}

export interface PlaybookVersionDetail extends PlaybookVersion {
  playbookName: string;
  playbookProductName: string;
  playbookDescription: string;
  playbookWorkflow: string;
  playbookSpecialInstructions: string;
  playbookApprovalNotes: string;
  playbookActiveTools: any[];
  playbookHelperAgents: any[];
  playbookApprovalTools: any[];
  playbookModel: string;
  playbookType?: string;
  playbookTags: string[];
  playbookChatType: string;
  playbookMaxTurns?: number;
  isCore: boolean;
  accessControl: any;
  ownerId: string;
}

export const usePlaybooksApi = () => {

  const { getAuthToken } = useAuth();
  const getHeaders = createApiHeadersGetter(getAuthToken);

  const buildPlaybook = async (playbookId: string): Promise<any> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${process.env.REACT_APP_AUTOGEN_BASE_URL || 'https://genie-autogen.azurewebsites.net'}/initialize-playbook`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          playbook_id: playbookId
        })
      });
      if (!response.ok) {
        // Log error but don't throw - let caller handle gracefully
        console.warn('Initialize playbook failed:', response.status, response.statusText);
        return { status: 'error', message: 'Failed to initialize playbook', error: response.statusText };
      }
      return response.json();
    } catch (error) {
      // Log error but don't throw - let caller handle gracefully
      console.warn('Error initializing playbook:', error);
      return { status: 'error', message: 'Failed to initialize playbook', error: String(error) };
    }
  };

  const getPlaybooks = async (
    getAllPlaybooks: boolean = false, 
    playbookType?: string,
    filters?: {
      tags?: string[];
      productNames?: string[];
      search?: string;
    }
  ): Promise<Playbook[]> => {
    try {
      const headers = await getHeaders();
      
      // Build query parameters
      const params = new URLSearchParams();
      if (getAllPlaybooks) {
        params.append('getAllPlaybooks', 'true');
      }
      if (playbookType) {
        params.append('playbook_type', playbookType);
      }
      
      // Add advanced filtering parameters
      if (filters) {
        if (filters.tags && filters.tags.length > 0) {
          params.append('tags', filters.tags.join(','));
        }
        if (filters.productNames && filters.productNames.length > 0) {
          params.append('product_names', filters.productNames.join(','));
        }
        if (filters.search && filters.search.trim()) {
          params.append('search', filters.search.trim());
        }
      }
      
      const queryString = params.toString();
      const url = queryString ? `${PLAYBOOKS_URL}?${queryString}` : PLAYBOOKS_URL;

      const response = await fetch(url, {
        method: 'GET',
        headers
      });
      if (!response.ok) {
        throw new Error('Failed to fetch Playbooks');
      }
      return response.json();
    } catch (error) {
      console.error('Error fetching Playbooks:', error);
      throw error;
    }
  };

  const getPlaybook = async (id: string): Promise<Playbook> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${id}`,
        {
          method: 'GET',
          headers
        }
      );
      console.log('[getPlaybook] Response:', response);

      if (!response.ok) {
        let errorMessage = 'Failed to fetch Playbook';
        let errorDetails;

        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          // If JSON parsing fails, use status text
          errorMessage = response.statusText || errorMessage;
        }

        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching Playbook:', error);
      // If it's already an ApiError, rethrow it
      if (error instanceof ApiError) {
        throw error;
      }
      // Otherwise wrap it in an ApiError
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const createPlaybook = async (playbookAgenticgenie: Omit<Playbook, 'id'>): Promise<Playbook> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify(playbookAgenticgenie),
        }
      );

      if (!response.ok) {
        console.log('[createPlaybook] Error creating Playbook response:', response);
        try {
          // Try to parse error response as JSON
          const errorData: APIError = await response.json();
          console.log('[createPlaybook] Error creating Playbook errorData:', errorData);
          throw new Error(errorData.message || 'Failed to create Playbook');
        } catch (parseError) {
          console.log('[createPlaybook] Error parsing JSON:', String(parseError));
          console.log('[createPlaybook] Error parsing JSON Type:', typeof (parseError));
          throw new Error(String(parseError));
        }
      }
      return response.json();
    } catch (error) {
      console.error('Error creating Playbook:', error);
      throw error;
    }
  };

  const updatePlaybook = async (playbookAgenticgenie: Partial<Playbook>): Promise<Playbook> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${playbookAgenticgenie.playbookId}`,
        {
          method: 'PUT',
          headers,
          body: JSON.stringify(playbookAgenticgenie),
        }
      );
      if (!response.ok) {
        throw new Error('Failed to update Playbook');
      }
      return response.json();
    } catch (error) {
      console.error('Error updating Playbook:', error);
      throw error;
    }
  };

  const deletePlaybook = async (id: string): Promise<void> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${id}`,
        {
          method: 'DELETE',
          headers,
        }
      );
      if (!response.ok) {
        throw new Error('Failed to delete Playbook');
      }
      return;
    } catch (error) {
      console.error('Error deleting Playbook:', error);
      throw error;
    }
  };

  const triggerPlaybook = async (playbookId: string, inputData: string): Promise<void> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${process.env.REACT_APP_AUTOGEN_BASE_URL + '/run-playbook' || 'https://eedgdv37ol7jjvvbjcbzn6n6rq0wvltc.lambda-url.us-east-1.on.aws/'}`, {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({

          playbook_id: playbookId,
          input: {
            data: inputData
          }

        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to trigger playbook';
        try {
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          throw new Error(errorMessage);
        } catch (parseError) {
          throw new Error(response.statusText || errorMessage);
        }
      }

      return;
    } catch (error) {
      console.error('Error triggering playbook:', error);
      throw error;
    }
  };

  const publishVersion = async (playbookId: string, changeNote?: string): Promise<PlaybookVersion> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${playbookId}/versions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ changeNote }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to publish version';
        let errorDetails;

        try {
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error publishing version:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const getVersions = async (playbookId: string): Promise<PlaybookVersion[]> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${playbookId}/versions`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to fetch versions';
        let errorDetails;

        try {
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching versions:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const getVersion = async (playbookId: string, version: number): Promise<PlaybookVersionDetail> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${playbookId}/versions/${version}`, {
        method: 'GET',
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to fetch version details';
        let errorDetails;

        try {
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error fetching version details:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  const rollbackToVersion = async (playbookId: string, version: number): Promise<PlaybookVersion> => {
    try {
      const headers = await getHeaders();
      const response = await fetch(`${PLAYBOOKS_URL}/${playbookId}/versions/${version}/rollback`, {
        method: 'POST',
        headers,
      });

      if (!response.ok) {
        let errorMessage = 'Failed to rollback to version';
        let errorDetails;

        try {
          const errorData: APIError = await response.json();
          errorMessage = errorData.message || errorMessage;
          errorDetails = errorData.details;
        } catch (parseError) {
          errorMessage = response.statusText || errorMessage;
        }

        throw new ApiError(errorMessage, response.status, errorDetails);
      }

      return await response.json();
    } catch (error: any) {
      console.error('Error rolling back to version:', error);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError(error.message || 'Unknown error occurred', 500);
    }
  };

  return {
    getPlaybooks,
    getPlaybook,
    createPlaybook,
    updatePlaybook,
    deletePlaybook,
    triggerPlaybook,
    buildPlaybook,
    publishVersion,
    getVersions,
    getVersion,
    rollbackToVersion
  };
}